import { useRef, useState } from 'react';

import { XIcon } from '@zola/zola-ui/src/components/SvgIconsV3/X';
import Portal from '@zola/zola-ui/src/components/util/Portal';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { StorefrontDetailsStickyFooter } from '~/pages/couples/storefronts/components/StorefrontDetailsStickyFooter';
import { useStorefrontDetails } from '~/pages/couples/storefronts/contexts/StorefrontDetailsContext';
import type { MappedGalleryPhotoView } from '~/types/responseTypes';
import type { StorefrontCoverGalleryPhoto } from '~/types/storefrontDetails';

import CachedImage from '../../images/CachedImage';
import {
  CloseLightboxBtn,
  Lightbox,
  LightboxImageContainer,
  ScreenReaderText,
  ModalContainer,
  MobileContainer,
  TitleBar,
  StyledTitle2,
  CLOSE_LIGHTBOX_ICON_SIZE,
} from './CoverGalleryV2.styles';

type DesktopLightBoxProps = {
  onClose: () => void;
};

const MobileLightbox = ({ onClose }: DesktopLightBoxProps) => {
  const { storefrontDetails } = useStorefrontDetails();
  const { coverGallery, photoGallery } = storefrontDetails || {};

  const photos: (StorefrontCoverGalleryPhoto | MappedGalleryPhotoView)[] = [
    ...(coverGallery as StorefrontCoverGalleryPhoto[]),
    ...(photoGallery as MappedGalleryPhotoView[]),
  ].filter(Boolean);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffectOnce(() => {
    if (document) {
      const pageContainer = document.getElementById('root');
      if (pageContainer) {
        setPortalContainer(pageContainer);
      }
    }
  });

  if (portalContainer === null) return null;

  return (
    <Portal portalContainer={portalContainer}>
      <Lightbox onClick={() => null} device="mobile">
        <LightboxImageContainer ref={containerRef} tabIndex={-1}>
          <ModalContainer>
            <TitleBar>
              <StyledTitle2 presentation="h6" color="WHITE_100">
                {storefrontDetails?.name}
              </StyledTitle2>
              <CloseLightboxBtn onClick={onClose}>
                <XIcon height={CLOSE_LIGHTBOX_ICON_SIZE} width={CLOSE_LIGHTBOX_ICON_SIZE} />
                <ScreenReaderText>Close</ScreenReaderText>
              </CloseLightboxBtn>
            </TitleBar>
            <MobileContainer>
              {photos.map(({ uuid }) => (
                <CachedImage
                  key={uuid}
                  uuid={uuid}
                  srcSizes={[{ width: 320 }, { width: 640 }]}
                  sizes="100vw"
                  fit="crop"
                  quality={60}
                />
              ))}
            </MobileContainer>
          </ModalContainer>
        </LightboxImageContainer>
        <StorefrontDetailsStickyFooter
          forceShow
          showFavoriteButton={false}
          showShareButton={false}
          onClick={onClose}
        />
      </Lightbox>
    </Portal>
  );
};

export default MobileLightbox;
